import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getEventDetails } from '@store/slices/eventSlice';
import { generatePasswordInbound, safeJSONParse } from './../fn';
import { getLocalStorage, setLocalStorage } from './inboundHelper';

export const useInbound = () => {
  const sluggedEvent = useSelector(getEventDetails)?.[0];
  const [inboundValue, setInboundValue] = useState<string | undefined>();

  useEffect(() => {
    const inbValue = getLocalStorage(`event-${sluggedEvent?.slug}`);
    setInboundValue(inbValue || undefined);
  }, [sluggedEvent?.slug]);

  const parsedInbound = useMemo(
    () => safeJSONParse(inboundValue),
    [inboundValue]
  );

  const isValidInboundForEvent = useMemo(() => {
    if (!parsedInbound || !sluggedEvent?.id) return false;
    const valueEvent = String(parsedInbound?.event ?? '');
    return valueEvent.length > 0 && valueEvent === String(sluggedEvent.id);
  }, [parsedInbound, sluggedEvent?.id]);

  const shouldShowForm = useMemo(() => {
    return !inboundValue || !isValidInboundForEvent;
  }, [inboundValue, isValidInboundForEvent]);

  const updateInboundValue = useCallback(
    (data?: Record<string, string>) => {
      if (!data?.event || !data || !sluggedEvent?.slug) return;

      const inboundData = {
        ...data,
      };

      const inboundName = `event-${sluggedEvent?.slug}`;
      const inboundValue = JSON.stringify(inboundData);

      setInboundValue(inboundValue);
      setLocalStorage(inboundName, inboundValue);
    },
    [sluggedEvent?.slug]
  );

  const registerInbound = useCallback(
    async (data: Record<string, string>, callback?: () => void) => {
      const formData = new URLSearchParams();
      formData.append('event', data.event);
      formData.append('path', data.path);
      formData.append('action', data.action);
      formData.append(
        'address_country',
        data?.address_country?.toUpperCase() ?? ''
      );
      formData.append('company_name', data?.company_name ?? '');
      formData.append('contact_phone', data?.contact_phone ?? '');
      formData.append('contact_mobile', data?.contact_mobile ?? '');
      formData.append('email', data?.email ?? '');
      formData.append('first_name', data?.first_name ?? '');
      formData.append('last_name', data?.last_name ?? '');
      formData.append('job_title', data?.job_title ?? '');
      formData.append('salutation', data?.salutation ?? '');
      formData.append('company_industry', data?.company_industry ?? '');
      formData.append(
        'company_type_of_business',
        data?.company_type_of_business ?? ''
      );
      formData.append('icebreaker', String(Boolean(data?.icebreaker)));
      formData.append('dinner', String(Boolean(data?.dinner)));
      formData.append('contact_type', String(data?.contact_type ?? 'del'));
      formData.append(
        'booking_package',
        String(data?.booking_package ?? 'OnsiteStaff')
      );

      const password = await generatePasswordInbound(data.email);

      await fetch(`/api/inbound/register`, {
        method: 'POST',
        headers: {
          Authorization: password,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      })
        .then(async response => await response.json())
        .then(data => {
          if (data?.errors) {
            console.error('Inbound registration errors:', data.errors);
          } else {
            const newInboundData = {
              ...(data?.data ?? {}),
              ...(data?.data?.client ?? {}),
            };
            delete newInboundData.client;
            updateInboundValue(newInboundData);

            const inboundRegistrationEvent = new CustomEvent(
              'inboundRegistrationComplete',
              {
                detail: newInboundData,
              }
            );
            window.dispatchEvent(inboundRegistrationEvent);

            if (callback) {
              callback();
            }
          }
        })
        .catch(e => {
          console.log('registerInbound error = ', e);
        });
    },
    [updateInboundValue]
  );

  const actionInbound = useCallback(
    async (
      data: Record<string, string | number | boolean | undefined>,
      callback?: () => void,
      endpoint = '/api/inbound/action'
    ): Promise<boolean> => {
      const formData = new URLSearchParams();
      formData.append('event', String(data.event ?? ''));
      formData.append('path', String(data.path ?? ''));
      formData.append('action', String(data.action ?? ''));
      formData.append(
        'address_country',
        String(
          (data?.address_country as string | undefined)?.toUpperCase() ?? ''
        )
      );
      formData.append('company_name', String(data?.company_name ?? ''));
      formData.append('contact_phone', String(data?.contact_phone ?? ''));
      formData.append('contact_mobile', String(data?.contact_mobile ?? ''));
      formData.append('email', String(data?.email ?? ''));
      formData.append('first_name', String(data?.first_name ?? ''));
      formData.append('job_title', String(data?.job_title ?? ''));
      formData.append('last_name', String(data?.last_name ?? ''));
      formData.append('salutation', String(data?.salutation ?? ''));
      formData.append('company_industry', String(data?.company_industry ?? ''));
      formData.append(
        'company_type_of_business',
        String(data?.company_type_of_business ?? '')
      );
      formData.append('icebreaker', String(Boolean(data?.icebreaker)));
      formData.append('dinner', String(Boolean(data?.dinner)));
      formData.append('contact_type', String(data?.contact_type ?? 'del'));
      formData.append(
        'booking_package',
        String(data?.booking_package ?? 'OnsiteStaff')
      );

      const password = await generatePasswordInbound(String(data?.email ?? ''));

      try {
        const response = await fetch(`${endpoint}`, {
          method: 'POST',
          headers: {
            Authorization: password,
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formData.toString(),
        });

        const result = await response.json();

        if (result?.errors) {
          console.error('Inbound action errors:', result.errors);
          return false;
        }

        const newInboundData = {
          ...(result?.data ?? {}),
          ...(result?.data?.client ?? {}),
        };
        delete newInboundData.client;
        updateInboundValue(newInboundData);

        if (callback) {
          callback();
        }

        return true;
      } catch (e) {
        console.log('actionInbound error = ', e);
        return false;
      }
    },
    [updateInboundValue]
  );

  return {
    inboundValue,
    updateInboundValue,
    actionInbound,
    registerInbound,
    email: safeJSONParse(inboundValue)?.email,
    shouldShowForm,
    isValidInboundForEvent,
    parsedInbound,
  };
};
